import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { DEPLOYMENT_STATUS_STYLES } from '../../constants';
import { EvalApiService } from '../../services/EvalApiService';
import { EvalModel } from '../../types';
import { fmtIso } from '../../utils';
import { PcModelModal, PcModelModalMode } from './PcModelModal';

interface ModalState {
  mode: PcModelModalMode;
  source: EvalModel | null;
}

export const PcConfigsListPage: React.FC = () => {
  const { token } = useAuthContext();
  const [models, setModels] = useState<EvalModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [modal, setModal] = useState<ModalState | null>(null);

  useEffect(() => {
    EvalApiService.listModels(token ?? '', { model_type: 'PC' })
      .then(({ items }) => setModels(items))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSaved = (model: EvalModel) => {
    setModels((prev) => {
      const exists = prev.some((m) => m.model_id === model.model_id);
      if (exists) return prev.map((m) => (m.model_id === model.model_id ? model : m));
      return [model, ...prev];
    });
    setModal(null);
  };

  // For Edit, we need the full detail (config field is included on list response too,
  // but fetching detail keeps us robust if the list ever drops config).
  const openEdit = async (model: EvalModel) => {
    const full = (await EvalApiService.getModel(token ?? '', model.model_id)) ?? model;
    setModal({ mode: 'edit', source: full });
  };

  const openClone = async (model: EvalModel) => {
    const full = (await EvalApiService.getModel(token ?? '', model.model_id)) ?? model;
    setModal({ mode: 'clone', source: full });
  };

  const filtered = models.filter((m) => !statusFilter || m.deployment_status === statusFilter);

  return (
    <div className="page-content">
      {modal && (
        <PcModelModal
          mode={modal.mode}
          source={modal.source}
          onSave={handleSaved}
          onClose={() => setModal(null)}
        />
      )}

      <div className="eval-page-header">
        <div>
          <div className="eval-page-title">PC Configs</div>
          <div className="eval-page-sub">
            Planogram Compliance model configurations. Configs are validated against
            <code style={{ margin: '0 4px' }}>PogComplianceConfig</code>and persisted in canonical
            form.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="eval-btn" onClick={() => setModal({ mode: 'create', source: null })}>
            + New PC Config
          </button>
        </div>
      </div>

      <div className="eval-filters">
        <select
          className="eval-filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="live">Live</option>
          <option value="staging">Staging</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Version</th>
              <th>Service Model ID</th>
              <th>Deployment Status</th>
              <th>Registered At</th>
              <th>Updated At</th>
              <th>Description</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.model_id}>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>
                  <Link to={`/eval/models/${m.model_id}`} style={{ color: 'var(--accent)' }}>
                    {m.version}
                  </Link>
                </td>
                <td
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                  }}
                >
                  {m.service_model_id}
                </td>
                <td>
                  <span style={{ ...DEPLOYMENT_STATUS_STYLES[m.deployment_status], fontSize: 12 }}>
                    {m.deployment_status.charAt(0).toUpperCase() + m.deployment_status.slice(1)}
                  </span>
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {fmtIso(m.registered_at)}
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {m.updated_at ? fmtIso(m.updated_at) : '—'}
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {m.description ?? '—'}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button
                    className="eval-btn-ghost"
                    style={{ padding: '4px 10px', fontSize: 11, marginRight: 6 }}
                    onClick={() => openEdit(m)}
                  >
                    Edit
                  </button>
                  <button
                    className="eval-btn-ghost"
                    style={{ padding: '4px 10px', fontSize: 11 }}
                    onClick={() => openClone(m)}
                  >
                    Clone
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 24 }}
                >
                  No PC configs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};
